import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { makeAutoObservable } from 'mobx';
import { isEffectiveDatePassed } from 'global/utils/dateHelper';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { RenewalAction } from 'types/domainServiceV2';
import { DomainProductLineItem } from './domainProductLinesDelegate';
import { RENEWAL_ACTIONS, RenewalActionType, RenewalActionDisabledReason, RenewalActionMap } from './types';

export interface RenewalActionsDelegateParams {
  getEffectiveDate: () => string;
  getInstalledProductLineIds: () => ProductLineId[];
  getDomainProductLineItems: () => DomainProductLineItem[];
}

interface RenewalActionConfig {
  isVisible: boolean;
  disabledReason: RenewalActionDisabledReason | null;
  productLineIds: ProductLineId[];
  accessLimitation?: string;
}

export class RenewalActionsDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _getEffectiveDate: () => string;
  private readonly _getInstalledProductLineIds: () => ProductLineId[];
  private readonly _getDomainProductLineItems: () => DomainProductLineItem[];

  /** Installed product lines that the domain still offers for the current configuration. */
  private get _includedProductLines(): ProductLineId[] {
    const domainProductLineIds = new Set(
      this._getDomainProductLineItems().map((productLineItem) => productLineItem.productLineId),
    );

    return this._getInstalledProductLineIds()
      .filter((productLineId) => domainProductLineIds.has(productLineId))
      .sort(sortByProductLineId);
  }

  /** Product lines the group can still add on top of the installed ones. */
  private get _availableProductLines(): ProductLineId[] {
    const includedProductLineIds = new Set(this._includedProductLines);

    return this._getDomainProductLineItems()
      .filter(
        (productLineItem) =>
          productLineItem.isAvailableWithCurrentConfiguration &&
          !includedProductLineIds.has(productLineItem.productLineId),
      )
      .map((productLineItem) => productLineItem.productLineId)
      .sort(sortByProductLineId);
  }

  /**
   * Actions config for the renewal actions.
   * This config is used to determine if the action is visible, disabled and what product lines are available for the action.
   * If the action is disabled, the disabled reason is returned.
   */
  private get _actionsConfig(): Record<RenewalActionType, RenewalActionConfig> {
    return {
      RENEW_AS_IS: {
        isVisible: this._rootStore.domainStoreV2.isRenewAsIsActionAvailable,
        disabledReason: this._getDisabledReason('RENEW_AS_IS'),
        productLineIds: this._includedProductLines,
      },
      ADD_BENEFIT: {
        isVisible: this._rootStore.domainStoreV2.isAddBenefitActionAvailable,
        disabledReason: this._getDisabledReason('ADD_BENEFIT'),
        productLineIds: this._availableProductLines,
      },
      CANCEL_COVERAGE: {
        isVisible: this._rootStore.domainStoreV2.isCancelCoverageActionAvailable,
        disabledReason: this._getDisabledReason('CANCEL_COVERAGE'),
        productLineIds: this._includedProductLines,
      },
      CHANGE_PLAN: {
        isVisible: this._rootStore.domainStoreV2.isChangePlanActionAvailable(this._rootStore.userStore.userRoles),
        disabledReason: this._getDisabledReason('CHANGE_PLAN'),
        productLineIds: this._includedProductLines,
        accessLimitation: this._isChangePlanListed ? '' : 'Renewal Management Only',
      },
    };
  }

  private _getDisabledReason(action: RenewalActionType): RenewalActionDisabledReason | null {
    const effectiveDate = this._getEffectiveDate();

    switch (action) {
      case 'ADD_BENEFIT':
        // If the retro-quoting period has passed, the user should not reach the Experience step with renewal actions shown.
        // This is a defensive check in case that restriction is bypassed elsewhere.
        if (effectiveDate !== '' && this._rootStore.domainStoreV2.isRetroQuotingPeriodPassed(effectiveDate)) {
          return 'RETRO_QUOTING_PERIOD_PASSED';
        }

        if (this._availableProductLines.length === 0) {
          return 'NO_ADDITIONAL_BENEFITS_AVAILABLE';
        }

        return null;

      case 'RENEW_AS_IS':
      case 'CANCEL_COVERAGE':
        return isEffectiveDatePassed(effectiveDate) ? 'EFFECTIVE_DATE_PASSED' : null;

      default:
        return null;
    }
  }

  private get _isChangePlanListed(): boolean {
    return (
      this._rootStore.domainStoreV2.domainHeader.properties?.availableRenewalActions?.includes(
        RenewalAction.CHANGE_PLAN,
      ) || false
    );
  }

  public get renewalActions(): RenewalActionMap[] {
    const actionsConfig = this._actionsConfig;

    return RENEWAL_ACTIONS.filter((action) => actionsConfig[action].isVisible).map((action) => ({
      action,
      disabledReason: actionsConfig[action].disabledReason,
      productLineIds: actionsConfig[action].productLineIds,
      accessLimitation: actionsConfig[action].accessLimitation,
    }));
  }

  /** False when every visible action is disabled, so the renewal cannot be continued. */
  public get hasAvailableActions(): boolean {
    return this.renewalActions.some(({ disabledReason }) => disabledReason === null);
  }

  constructor({
    getEffectiveDate,
    getInstalledProductLineIds,
    getDomainProductLineItems,
  }: RenewalActionsDelegateParams) {
    this._getEffectiveDate = getEffectiveDate;
    this._getInstalledProductLineIds = getInstalledProductLineIds;
    this._getDomainProductLineItems = getDomainProductLineItems;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
